import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Stethoscope, Activity, LineChart, Sparkles, ShieldCheck, ArrowRight,
} from "lucide-react";
import { NewsTicker } from "@/knowmeno/news/NewsTicker";
import { LocaleSwitcher } from "@/knowmeno/marketing/LocaleSwitcher";
import { es } from "@/i18n/es";

export const Route = createFileRoute("/es/medical-reset")({
  head: () => ({
    meta: [
      { title: "Reset Médico — Síntomas y etapa | SepaMeno" },
      {
        name: "description",
        content:
          "Reset Médico combina seguimiento diario con un Reporte Inteligente de Síntomas clínico — para que tu doctora trate la etapa de menopausia real.",
      },
      { property: "og:title", content: "Reset Médico — La nueva forma de tratar la menopausia" },
      { property: "og:description", content: "Reporte Inteligente de Síntomas + especialistas — sincronización con wearables próximamente." },
      { property: "og:locale", content: "es" },
      { name: "robots", content: "index, follow" },
    ],
    links: [
      { rel: "canonical", href: "https://sepameno.com/medical-reset" },
      { rel: "alternate", hrefLang: "es", href: "https://sepameno.com/medical-reset" },
      { rel: "alternate", hrefLang: "en", href: "https://knowmeno.com/medical-reset" },
      { rel: "alternate", hrefLang: "x-default", href: "https://knowmeno.com/medical-reset" },
    ],
  }),
  notFoundComponent: () => <div className="p-8">No encontrado.</div>,
  errorComponent: () => <div className="p-8">Algo salió mal.</div>,
  component: MedicalResetEsPage,
});

function MedicalResetEsPage() {
  const t = es.medicalReset;
  return (
    <div className="min-h-screen bg-background" lang="es">
      <header className="border-b">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/es" className="text-sm text-muted-foreground hover:text-foreground">
            {t.back}
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 font-serif text-xl">
              <Stethoscope className="h-5 w-5" /> Reset Médico
            </div>
            <LocaleSwitcher />
          </div>
        </div>
      </header>

      <NewsTicker />

      <section className="mx-auto max-w-6xl px-4 pt-12 pb-10 sm:pt-20">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
            <Sparkles className="h-3 w-3" /> {t.eyebrow}
          </span>
          <h1
            className="mt-4 font-serif text-4xl leading-tight sm:text-5xl"
            dangerouslySetInnerHTML={{ __html: t.title }}
          />
          <p className="mt-4 text-lg text-muted-foreground">{t.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/sign-up"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            >
              {t.ctaPrimary} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/es/tienda"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold hover:bg-muted"
            >
              {t.ctaSecondary}
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10">
        <div className="grid gap-6 sm:grid-cols-3">
          {t.pillars.map((p, i) => {
            const icon = i === 0 ? <Activity className="h-6 w-6" /> : i === 1 ? <LineChart className="h-6 w-6" /> : <Stethoscope className="h-6 w-6" />;
            const cta = "cta" in p ? p.cta : undefined;
            return (
              <div key={i} className={`rounded-lg border p-6 ${i === 1 ? "border-primary bg-primary/5" : "bg-card"}`}>
                <div className="text-primary">{icon}</div>
                <h3 className="mt-3 font-serif text-lg">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{p.body}</p>
                {cta && (
                  <Link to={cta.to} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                    {cta.label} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="border-y bg-muted/30">
        <div className="mx-auto max-w-6xl px-4 py-14">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                Lo central
              </span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl">{t.reportTitle}</h2>
              <p className="mt-4 text-muted-foreground">{t.reportIntro}</p>
              <ul className="mt-6 space-y-3 text-sm">
                {t.reportRows.map(([label, body], i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>
                      <strong>{label}.</strong>{" "}
                      <span className="text-muted-foreground">{body}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4" /> {t.disclaimer}
              </p>
            </div>

            <div className="rounded-lg border bg-card p-6 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t.whyTitle}
              </div>
              <ol className="mt-4 space-y-3 text-sm">
                {t.whyRows.map((row, i) => (
                  <li key={i}><strong>{i + 1}.</strong> {row}</li>
                ))}
              </ol>
              <Link
                to="/sign-up"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              >
                {t.whyCta} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-14">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl">{t.newsTitle}</h2>
            <p className="mt-2 text-muted-foreground">{t.newsSub}</p>
          </div>
          <Link to="/es/noticias" className="text-sm font-semibold text-primary hover:underline">
            Ver todas →
          </Link>
        </div>
        <div className="mt-6">
          <NewsTicker />
        </div>
      </section>

      <footer className="border-t">
        <div className="mx-auto max-w-6xl px-4 py-8 text-xs text-muted-foreground">
          {t.footer}
        </div>
      </footer>
    </div>
  );
}
